import React from "react";
import PolicyLayout from "../components/PolicyLayout";
import { useSeo } from "../lib/seo";

/**
 * Plain-English summary of Zcardon's privacy practices. Drafted to satisfy
 * the visibility requirements Twilio's TFV review looks for (named SMS
 * data-handling, no data sale, third-party processors disclosed) and the
 * baseline a US-targeted fintech needs (PII categories, GLBA/CCPA mentions).
 * NOT legal advice — bracketed placeholders MUST be filled in by counsel
 * before production launch.
 */
export default function PrivacyPolicy() {
  useSeo({
    title: "Privacy Policy",
    description: "How Zcardon collects, uses, stores, and protects your personal information, including phone numbers used for SMS verification and card notifications.",
    path: "/privacy",
  });
  return (
    <PolicyLayout
      eyebrow="Company Policy"
      title="Privacy Policy"
      effective="Feb 1, 2026"
      lastUpdated="Feb 6, 2026"
      testId="privacy-policy-page"
    >
      <p>
        This Privacy Policy describes how <strong>Zeborg Water Solution Corp.</strong>{" "}
        (&quot;<strong>Zcardon</strong>&quot;, &quot;we&quot;, &quot;our&quot;,
        or &quot;us&quot;) collects, uses, discloses, and protects information
        about you when you use the Zcardon virtual debit card platform at{" "}
        <a href="https://zcardon.com">zcardon.com</a> (the &quot;Service&quot;).
      </p>

      <h2>1. Information we collect</h2>
      <h3>Information you provide</h3>
      <ul>
        <li><strong>Account information</strong> — name, email address, mobile phone number, date of birth, and a password you create.</li>
        <li><strong>Identity verification (KYC)</strong> — government-issued ID image, last-4 of SSN (encrypted at rest using AES via Fernet), and home address. We collect this only where required by US financial regulations or by our card-issuing partners.</li>
        <li><strong>Payment information</strong> — bank account details (for ACH funding) and card details, processed via Stripe. We never store full card numbers on our servers.</li>
        <li><strong>Recipient information</strong> — when you issue a virtual card to a receiver, you provide their name and (optionally) their phone number so we can deliver the card link.</li>
        <li><strong>Agent application data</strong> — driver&apos;s license images (front + back), SSN, and contact information, encrypted at rest.</li>
      </ul>

      <h3>Information collected automatically</h3>
      <ul>
        <li>Device information (browser type, OS, IP address) and basic usage logs, used for fraud detection and rate-limiting.</li>
        <li>Cookies and similar technologies for session management. We do not use third-party advertising cookies.</li>
      </ul>

      <h2>2. How we use your information</h2>
      <ul>
        <li>To create and maintain your account and issue virtual cards.</li>
        <li>To send <strong>transactional SMS</strong> — verification codes (2FA), password-reset codes, card-delivery links, and account-event notifications. See our <a href="/sms-policy">SMS Policy</a> for details.</li>
        <li>To process payments via Stripe and ACH partners.</li>
        <li>To comply with anti-money-laundering (AML), Bank Secrecy Act (BSA), and OFAC sanctions-screening requirements.</li>
        <li>To detect, prevent, and investigate fraud, abuse, or violations of our <a href="/terms">Terms of Service</a>.</li>
      </ul>

      <h2>3. SMS and phone-number handling</h2>
      <p>
        Phone numbers collected at sign-up, password reset, agent application,
        or card-delivery are used <strong>exclusively</strong> for the
        transactional message types listed in our <a href="/sms-policy">SMS Policy</a>.
        We do <strong>not</strong> send marketing or promotional SMS, and we
        do <strong>not</strong> sell, rent, or share mobile phone numbers with
        third parties for their own marketing.
      </p>
      <p>
        Phone numbers are stored encrypted. You may opt out at any time by
        replying <code>STOP</code> to any Zcardon SMS, which removes you from
        all SMS lists immediately (a confirmation will be sent).
      </p>

      <h2>4. Third-party service providers</h2>
      <p>We share information only with the processors that power our Service:</p>
      <ul>
        <li><strong>Stripe, Inc.</strong> — payment processing, card issuing (planned), Stripe Identity for KYC.</li>
        <li><strong>Twilio Inc.</strong> — SMS delivery, two-factor authentication codes (Verify), MMS for card-image delivery.</li>
        <li><strong>Resend</strong> — transactional email delivery.</li>
        <li><strong>MongoDB Atlas / cloud database providers</strong> — encrypted data storage.</li>
        <li><strong>Wise (TransferWise) / Nium</strong> — international payouts (where applicable).</li>
      </ul>
      <p>Each processor is contractually bound to use your data only to provide services to Zcardon.</p>

      <h2>5. Data retention</h2>
      <p>
        We retain account data for as long as your account is active, and for
        up to <strong>7 years</strong> after closure to comply with US
        financial record-keeping requirements (BSA §1010.430). Audit logs and
        AML-related records may be retained longer where legally required.
      </p>

      <h2>6. Your rights</h2>
      <p>
        Depending on your state of residence, you may have the right to access,
        correct, delete, or port your personal information; to opt out of
        certain processing; and to non-discrimination for exercising these
        rights. To exercise any right, email{" "}
        <a href="mailto:privacy@zcardon.com">privacy@zcardon.com</a>.
        California residents — see our CCPA notice below.
      </p>

      <h3>California (CCPA / CPRA)</h3>
      <p>
        In the prior 12 months we collected: identifiers (name, email, phone),
        commercial information (transactions), internet activity (usage logs),
        geolocation (approximate, from IP), and financial information (bank /
        card data via Stripe). We <strong>do not sell</strong> personal
        information and we <strong>do not share</strong> it for cross-context
        behavioral advertising.
      </p>

      <h2>7. Security</h2>
      <p>
        We use TLS 1.2+ in transit, AES-128 at rest for highly sensitive
        fields (SSN, driver&apos;s license imagery), bcrypt for password
        hashing, and least-privilege access controls. No system is perfectly
        secure — please use a unique, strong password and enable 2FA.
      </p>

      <h2>8. Children</h2>
      <p>
        Zcardon is not directed to anyone under 18. We do not knowingly
        collect data from minors. If you believe a minor has provided us
        information, contact <a href="mailto:privacy@zcardon.com">privacy@zcardon.com</a> and we will delete it.
      </p>

      <h2>9. Changes to this policy</h2>
      <p>
        We may update this Privacy Policy from time to time. Material changes
        will be announced via email and on this page at least 30 days before
        taking effect.
      </p>

      <h2>10. Contact us</h2>
      <p>
        <strong>Zeborg Water Solution Corp.</strong><br />
        2223 Arundel Crossing Dr, Sugar Land, TX 77479<br />
        Privacy questions: <a href="mailto:privacy@zcardon.com">privacy@zcardon.com</a><br />
        General support: <a href="mailto:hello@zcardon.com">hello@zcardon.com</a> · <a href="/support">/support</a>
      </p>
    </PolicyLayout>
  );
}
